import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReduxActionTypes } from "ee/constants/ReduxActionConstants";
import {
  getCurrentApplicationId,
  previewModeSelector,
} from "selectors/editorSelectors";
import { getSelectedWidgets } from "selectors/ui";
import { createMessage } from "ee/constants/messages";
import { startKeyboardMove } from "../keyboardDnD/keyboardDnDEvents";
import {
  isAnvilDebugEnabled,
  setAnvilDebug,
} from "../../common/logging/anvilDebug";
import {
  COMMAND_GROUP_DEVELOPER,
  COMMAND_GROUP_EDITOR,
  DEPLOY_APPLICATION_LABEL,
  EXIT_PREVIEW_MODE_LABEL,
  MOVE_WIDGET_LABEL,
  TOGGLE_ANVIL_DEBUG_LOGGING_LABEL,
  TOGGLE_PREVIEW_MODE_LABEL,
} from "../../common/messages";
import type { AnvilCommand } from "./types";

/**
 * Builds the Anvil command set wired to existing, safe editor actions.
 * Kept intentionally small for the M3.1 MVP (preview toggle + deploy); extend
 * with navigation / add-widget commands in later increments.
 */
export function useAnvilCommands(): AnvilCommand[] {
  const dispatch = useDispatch();
  const applicationId = useSelector(getCurrentApplicationId);
  const isPreviewMode = useSelector(previewModeSelector);
  const selectedWidgets = useSelector(getSelectedWidgets);

  return useMemo<AnvilCommand[]>(
    () => [
      // Keyboard DnD lift (design v2): the palette is the discoverability
      // anchor for keyboard move mode. Offered only for a single selection.
      ...(selectedWidgets.length === 1
        ? [
            {
              id: "move-widget",
              label: createMessage(MOVE_WIDGET_LABEL),
              group: createMessage(COMMAND_GROUP_EDITOR),
              keywords: ["move", "reorder", "drag", "keyboard", "position"],
              // The move-mode controller announces lift via its live region.
              silentToast: true,
              run: () => startKeyboardMove(selectedWidgets[0]),
            },
          ]
        : []),
      {
        id: "toggle-preview",
        label: isPreviewMode
          ? createMessage(EXIT_PREVIEW_MODE_LABEL)
          : createMessage(TOGGLE_PREVIEW_MODE_LABEL),
        group: createMessage(COMMAND_GROUP_EDITOR),
        keywords: ["preview", "view"],
        // Mirrors setPreviewModeInitAction (actions/editorActions); inlined to
        // avoid pulling that module's import graph into this Anvil-editor leaf
        // (cyclic-dep break). Keep the payload shape in lockstep with it.
        run: () =>
          dispatch({
            type: ReduxActionTypes.SET_PREVIEW_MODE_INIT,
            payload: !isPreviewMode,
          }),
      },
      {
        id: "deploy",
        label: createMessage(DEPLOY_APPLICATION_LABEL),
        group: createMessage(COMMAND_GROUP_EDITOR),
        keywords: ["publish", "ship", "release"],
        // The deploy flow surfaces its own progress/result feedback.
        silentToast: true,
        // Mirrors publishAnvilApplication (ce/actions/applicationActions);
        // inlined for the same cyclic-dep reason as toggle-preview above. Keep
        // the payload shape in lockstep with it.
        run: () =>
          dispatch({
            type: ReduxActionTypes.PUBLISH_ANVIL_APPLICATION_INIT,
            payload: { applicationId },
          }),
      },
      {
        id: "toggle-anvil-debug",
        label: createMessage(TOGGLE_ANVIL_DEBUG_LOGGING_LABEL),
        group: createMessage(COMMAND_GROUP_DEVELOPER),
        keywords: ["debug", "log", "logging", "verbose", "diagnostics"],
        run: () => setAnvilDebug(!isAnvilDebugEnabled()),
      },
    ],
    [dispatch, applicationId, isPreviewMode, selectedWidgets],
  );
}
